/**
 * One-time recovery of pre-isolation device-mirror keys.
 *
 * Before diagrams were isolated, a board's on-device copy lived under UNSCOPED localStorage
 * keys:
 *   libredraw:pages                the page list
 *   libredraw:page:<pageId>        a page's elements
 *   libredraw:comments:<pageId>    a page's comment threads
 * Isolation re-keyed these by diagram id (...:<diagramId>:...). That re-key ORPHANED any content
 * a user had saved before the change on the local-first / offline path - it was still on
 * disk, but under keys nothing read any more. This migrates those legacy keys onto the
 * "default" diagram scope (what `/canvas` with no ?diagram= resolves to) exactly once, so
 * pre-existing on-device work reappears instead of vanishing.
 *
 * Idempotent and non-destructive: it never overwrites a scoped key that already exists, and
 * it is a no-op after the first run (guarded by a marker key). Signed-in accounts keep the
 * authoritative copy in the graph - this only rescues the device mirror.
 */
const DONE_KEY = "libredraw:mirrorMigrated";

/** Move src→dst without ever clobbering an existing scoped copy, then drop the legacy key. */
function moveKey(src: string, dst: string): void {
  const v = localStorage.getItem(src);
  if (v === null) return;
  if (localStorage.getItem(dst) === null) localStorage.setItem(dst, v);
  localStorage.removeItem(src);
}

export function migrateLegacyMirror(): void {
  try {
    if (typeof localStorage === "undefined") return;
    if (localStorage.getItem(DONE_KEY)) return;
    // snapshot the keys first: moveKey mutates localStorage as we go
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) { const k = localStorage.key(i); if (k) keys.push(k); }
    for (const k of keys) {
      const p = k.split(":");
      // legacy page list      libredraw:pages            -> libredraw:pages:default
      if (k === "libredraw:pages") moveKey(k, "libredraw:pages:default");
      // legacy page elements   libredraw:page:<pid>       -> libredraw:page:default:<pid>
      // (exactly 3 segments - the isolated form libredraw:page:<did>:<pid> has 4, so it is left alone)
      else if (p.length === 3 && p[0] === "libredraw" && p[1] === "page") moveKey(k, `libredraw:page:default:${p[2]}`);
      // legacy comments        libredraw:comments:<pid>   -> libredraw:comments:default:<pid>
      else if (p.length === 3 && p[0] === "libredraw" && p[1] === "comments") moveKey(k, `libredraw:comments:default:${p[2]}`);
    }
    localStorage.setItem(DONE_KEY, "1");
  } catch { /* storage disabled / quota - recovery is best-effort, never fatal to boot */ }
}

// Referenced from EditorShell.cl.jac so the jac client build bundles this module.
export const LEGACY_MIRROR_MODULE = true;
